import { useEffect, useState } from "react";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserConsentContext } from "lib/contexts/UserConsentProvider";
import { Consent, ConsentAnswers } from "lib/types/consent";
import { getAllAnswers, MARKETING_CONSENT_CODE } from "lib/utils/consent";

/**
 * State for the login screen consent UI: the main checkbox, the marketing
 * checkbox, the Manage preferences view (edited as a draft until Save) and the
 * Read more view.
 */
export const useConsentForm = () => {
  const {
    state: {
      consentState: { isConsentEnabled, visibleConsents = [], marketingConsent = null },
      checkoutMetadata,
      cartMetadata,
      ssoMetadata,
    },
  } = useMerchantContext();
  const {
    state: { answers },
    actions: { loadAnswersFromShopify, saveAnswers },
  } = useUserConsentContext();

  // Not tied to login: consent enabled on merchant-apps and the merchant has consents to show.
  // The same ui-attribute that shows the old MarketingConsent checkbox.
  const isMarketingFlagEnabled =
    checkoutMetadata?.uiAttributes?.layout?.enableMarketingFlag ??
    cartMetadata?.uiAttributes?.layout?.enableMarketingFlag ??
    ssoMetadata?.layout?.enable_marketing_flag ??
    true;
  const isMarketingVisible = Boolean(marketingConsent) && isMarketingFlagEnabled;

  const isVisible = isConsentEnabled && (visibleConsents.length > 0 || isMarketingVisible);

  // Once, when the section becomes visible (the consents have loaded by then).
  useEffect(() => {
    if (isVisible) loadAnswersFromShopify();
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const optionalConsents = visibleConsents.filter((consent) => !consent.is_mandatory);
  const isAllAccepted =
    optionalConsents.length > 0 && optionalConsents.every((consent) => Boolean(answers[consent.code]));

  // Every toggle on or off at once, synced to Shopify straight away.
  const setAllAccepted = (isAccepted: boolean) =>
    saveAnswers(getAllAnswers(visibleConsents, isAccepted, answers));

  // MARKETING and all its channels follow this one checkbox (see getConsentUpdatesFromAnswers).
  const isMarketingAccepted = Boolean(answers[MARKETING_CONSENT_CODE]);
  const setMarketingAccepted = (isAccepted: boolean) =>
    saveAnswers({ ...answers, [MARKETING_CONSENT_CODE]: isAccepted });

  const [draft, setDraft] = useState<ConsentAnswers | null>(null);
  const [detailConsent, setDetailConsent] = useState<Consent | null>(null);

  const openPreferences = () => setDraft({ ...answers });
  const closePreferences = () => {
    setDraft(null);
    setDetailConsent(null);
  };
  const changeDraft = (code: string, isAccepted: boolean) =>
    setDraft((current) => ({ ...(current ?? {}), [code]: isAccepted }));
  const savePreferences = () => {
    // The draft only edits the listed consents; hidden ones and MARKETING carry over.
    if (draft) saveAnswers({ ...answers, ...draft });
    closePreferences();
  };

  return {
    isVisible,
    visibleConsents,
    isAllAccepted,
    setAllAccepted,
    marketingConsent,
    isMarketingVisible,
    isMarketingAccepted,
    setMarketingAccepted,
    isPreferencesOpen: draft !== null,
    draft: draft ?? answers,
    openPreferences,
    closePreferences,
    changeDraft,
    savePreferences,
    detailConsent,
    openDetail: setDetailConsent,
    closeDetail: () => setDetailConsent(null),
  };
};

export type ConsentForm = ReturnType<typeof useConsentForm>;
